import { useQuery, UseQueryResult } from "react-query";
import {
  GET_COLLABORATION_NOTES,
  STALE_TIME_5_MINUTES,
} from "@constants/reactQueryKeys";
import { getCollaborationNotes } from "api/collaborationNotes";
import type { CollaborationNote } from "types/collaborationNote";

interface UseGetCollaborationNotesReturn {
  notes: CollaborationNote[];
  notesLoading: boolean;
  refetchNotes: () => void;
  hasRecentCollaboration: boolean;
}

/**
 * Fetches collaboration notes for a given client-provider pair.
 * Query fires eagerly by default; pass `enabled: false` to defer until needed.
 *
 * @param clientId - The client ID.
 * @param providerId - The provider ID.
 * @param options - Optional config. `enabled` gates the query (default: true).
 */
const useGetCollaborationNotes = (
  clientId: string | undefined,
  providerId: string | undefined,
  options?: { enabled?: boolean },
): UseGetCollaborationNotesReturn &
  Omit<UseQueryResult, "data" | "isLoading" | "refetch"> => {
  const { data, isLoading, refetch, ...rest } = useQuery(
    [GET_COLLABORATION_NOTES, clientId, providerId],
    () => getCollaborationNotes(clientId!, providerId!),
    {
      enabled:
        Boolean(clientId) && Boolean(providerId) && (options?.enabled ?? true),
      staleTime: STALE_TIME_5_MINUTES,
      retry: false,
    },
  );

  const extractNotes = (payload: unknown): CollaborationNote[] => {
    if (Array.isArray(payload)) return payload as CollaborationNote[];

    if (payload && typeof payload === "object") {
      const nested = (payload as Record<string, unknown>).data;
      if (Array.isArray(nested)) return nested as CollaborationNote[];
    }

    return [];
  };

  const notes = extractNotes(data?.data);

  return {
    notes,
    notesLoading: isLoading,
    refetchNotes: refetch,
    hasRecentCollaboration: notes.some((n) => n.isRecentCollaboration),
    ...rest,
  };
};

export default useGetCollaborationNotes;
